import * as React from 'react';
import { ICellRendererParams } from '@ag-grid-community/core';
import { style, classes } from 'typestyle';
import { intersection, isArray, isEmpty, isEqual } from 'lodash';
import { ClientDataApi } from 'configuration-schemas/confdefnView';
import { getValidValues } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.client';
import { getUrl } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import { Box } from '@material-ui/core';

export const ValidSizesEditorStyles = {
  validsizesContainer: style({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    justifyContent: 'space-between',
    width: '250px',
    height: '150px',
    padding: '5px',
    border: '1px solid rgb(204, 204, 204)',
    borderRadius: '4px',
  }),
  buttonsContainer: style({
    display: 'flex',
    justifyContent: 'flex-end',
  }),
  button: style({
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    backgroundColor: '#f5f5f5',
    cursor: 'pointer',
    margin: '0 5px',
  }),
  sizesList: style({
    display: 'flex',
    flexWrap: 'wrap',
    overflowX: 'auto',
    overflowY: 'auto',
    height: '100px',
  }),
  validsize: style({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
    border: '1px lightgrey solid',
    margin: '3px 1px',
    padding: '0 2px',
    height: '22px',
  }),
  checked: style({}),
  unchecked: style({
    border: '1px lightgrey solid',
    background: 'lightgrey',
    color: 'grey',
  }),
  disabled: style({
    cursor: 'not-allowed',
    color: '#555',
  }),
};

interface ValidSizesProps extends ICellRendererParams {
  dataApi?: ClientDataApi;
  options?: string[];
  cursorStyle?: string;
  selectedOptions: string[];
}

interface ValidSizesState {
  dataApiOptions: string[];
  localSelections: string[];
}

export class ValidSizesEditor extends React.Component<ValidSizesProps, ValidSizesState> {
  constructor(props: ValidSizesProps) {
    super(props);
    this.state = {
      dataApiOptions: [],
      localSelections: this.props.value || [],
    };
  }

  componentDidMount() {
    const selections: string[] = this.props.getValue ? this.props.getValue() : [];
    if (this.props.dataApi) {
      const url = getUrl(this.props.dataApi);
      getValidValues(url, false, false, false)
        .then((validValues) => {
          this.setState({
            dataApiOptions: validValues.map((i: { value: string }) => i.value),
          });
        })
        .catch((err) => window.console.log(err));
    } else {
      if (isEmpty(selections) || intersection(selections, this.props.options).length < selections.length) {
        this.selectAllOptions();
      }
    }
  }

  componentDidUpdate(oldProps: ValidSizesProps) {
    if (!isEqual(oldProps.options, this.props.options)) {
      this.selectAllOptions();
    } else if (!isEqual(oldProps.dataApi, this.props.dataApi) && this.props.dataApi != null) {
      const url = getUrl(this.props.dataApi);
      getValidValues(url, false, false, false)
        .then((validValues) => {
          const selections = validValues.map((i: { value: string }) => i.value);
          this.setState(
            {
              dataApiOptions: validValues.map((i: { value: string }) => i.value),
              localSelections: selections,
            },
            () => {
              this.props.setValue && this.props.setValue(selections);
            },
          );
        })
        .catch((err) => window.console.log(err));
    }
  }

  getOptions() {
    const selections: string[] = this.props.getValue ? this.props.getValue() : [];
    if (this.props.dataApi != null) {
      return this.state.dataApiOptions;
    } else if (this.props.options != null) {
      return this.props.options;
    } else if (isArray(selections)) {
      return selections as string[];
    } else {
      return [];
    }
  }

  selectAllOptions = () => {
    const options = this.getOptions();
    this.setState({ localSelections: options });
  };

  onClickOption = (optionClicked: string) => {
    const options = this.getOptions();
    let newSelections = [...this.state.localSelections];

    if (options && options.length > 0) {
      newSelections = newSelections.filter((selection) => {
        return options.indexOf(selection) !== -1;
      });
    }

    if (newSelections.indexOf(optionClicked) === -1) {
      newSelections.push(optionClicked);
    } else {
      newSelections = newSelections.filter((selection) => selection !== optionClicked);
    }

    this.setState({ localSelections: newSelections });
  };

  getValue(): string[] {
    return this.state.localSelections;
  }

  clearAllOptions = () => {
    this.setState({ localSelections: [] });
  };

  render() {
    const renderedOptions = this.getOptions();

    return (
      <Box className={ValidSizesEditorStyles.validsizesContainer}>
        <div className={ValidSizesEditorStyles.sizesList}>
          {renderedOptions.map((option) => {
            const className =
              this.state.localSelections.indexOf(option) !== -1
                ? `${ValidSizesEditorStyles.validsize}`
                : `${ValidSizesEditorStyles.validsize} ${ValidSizesEditorStyles.unchecked}`;
            return (
              <div
                className={classes(className)}
                style={{ cursor: this.props.cursorStyle }}
                key={option}
                onClick={() => this.onClickOption(option)}
              >
                {option}
              </div>
            );
          })}
        </div>
        <div className={ValidSizesEditorStyles.buttonsContainer}>
          <button className={ValidSizesEditorStyles.button} onClick={this.clearAllOptions}>
            Clear All
          </button>
          <button className={ValidSizesEditorStyles.button} onClick={this.selectAllOptions}>
            Add All
          </button>
        </div>
      </Box>
    );
  }
}
